/**
 * Shopacado Discount Table Block
 * Initializes discount table blocks on product pages
 */
(function() {
  'use strict';

  // Cache for original product price - stored on window to persist across script re-runs
  // This prevents price recalculation when cart operations cause DOM updates
  if (typeof window.shopacadoCachedProductPrice === 'undefined') {
    window.shopacadoCachedProductPrice = null;
  }

  // Track the current product URL to detect navigation changes
  if (typeof window.shopacadoLastProductUrl === 'undefined') {
    window.shopacadoLastProductUrl = window.location.pathname;
  }

  // Clear cache if URL changed (AJAX navigation)
  if (window.shopacadoLastProductUrl !== window.location.pathname) {
    window.shopacadoCachedProductPrice = null;
    window.shopacadoLastProductUrl = window.location.pathname;
  }

  /**
   * Inject CSS styles for the discount table
   */
  function injectStyles() {
    // Check if styles already injected
    if (document.getElementById('shopacado-table-styles')) {
      return;
    }

    const style = document.createElement('style');
    style.id = 'shopacado-table-styles';
    style.textContent = `
      /* Hide table until JavaScript initialization completes */
      .shopacado-discount-table-block:not([data-shopacado-initialized="true"]) {
        opacity: 0 !important;
        visibility: hidden !important;
      }

      .shopacado-discount-table-block[data-shopacado-initialized="true"] {
        opacity: 1 !important;
        visibility: visible !important;
        transition: opacity 0.2s ease-in !important;
      }

      /* Loading spinner styles */
      .shopacado-table-loading {
        display: flex !important;
        flex-direction: column !important;
        align-items: center !important;
        justify-content: center !important;
        min-height: 200px !important;
        padding: 40px 20px !important;
        background: #fff !important;
        border-radius: 10px !important;
        transition: opacity 0.2s ease-out !important;
      }

      .shopacado-discount-table-block[data-shopacado-initialized="true"] .shopacado-table-loading {
        opacity: 0 !important;
        visibility: hidden !important;
        position: absolute !important;
        pointer-events: none !important;
      }

      /* Spinner animation */
      .shopacado-spinner {
        width: 40px !important;
        height: 40px !important;
        border: 4px solid #A9D9C5 !important;
        border-top-color: #0F6B6B !important;
        border-radius: 50% !important;
        animation: shopacado-spin 0.8s linear infinite !important;
      }

      @keyframes shopacado-spin {
        to { transform: rotate(360deg); }
      }

      .shopacado-loading-text {
        margin-top: 16px !important;
        color: #0F6B6B !important;
        font-size: 14px !important;
        font-weight: 600 !important;
      }

      /* Shopacado Discount Table Styles */
      div.shopacado-discount-table-block > h4,
      .shopacado-discount-table-header h4 {
        text-align: center !important;
        background: #759c98 !important;
        color: white !important;
        font-weight: 700 !important;
        height: 30px !important;
        line-height: 1.6 !important;
        font-size: 16px !important;
        border-top-left-radius: 5px !important;
        border-top-right-radius: 5px !important;
        margin: 0 !important;
      }

      table.shopacado-discount-table {
        table-layout: fixed !important;
        width: 100% !important;
      }

      table.shopacado-discount-table th {
        background: black !important;
        color: white !important;
        font-weight: 700 !important;
        font-size: 13px !important;
        height: 30px !important;
        text-align: center !important;
      }

      table.shopacado-discount-table th:first-child {
        border-bottom-left-radius: 5px !important;
      }

      table.shopacado-discount-table th:last-child {
        border-bottom-right-radius: 5px !important;
      }

      table.shopacado-discount-table td {
        text-align: center !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        height: 30px !important;
        color: black !important;
      }
    `;

    document.head.appendChild(style);
  }

  /**
   * Apply inline styles to h4 element as backup
   * @param {HTMLElement} element - The discount table block element
   */
  function applyInlineStyles(element) {
    const h4 = element.querySelector('h4');
    if (h4) {
      // Always set text to "Buy More, Pay Less" regardless of original content
      h4.textContent = 'Buy More, Pay Less';

      h4.style.textAlign = 'center';
      h4.style.background = '#0F6B6B';
      h4.style.color = 'white';
      h4.style.fontWeight = '700';
      h4.style.height = '30px';
      h4.style.lineHeight = '1.6';
      h4.style.fontSize = '16px';
      h4.style.borderTopLeftRadius = '5px';
      h4.style.borderTopRightRadius = '5px';
      h4.style.margin = '0';
    }

    const table = element.querySelector('.shopacado-discount-table');
    if (table) {
      table.style.tableLayout = 'fixed';
      table.style.width = '100%';
    }
  }

  /**
   * Parse price from text (e.g., "£29.99" -> 2999 cents)
   * @param {string} priceText - Price text to parse
   * @returns {number|null} Price in cents or null if parsing failed
   */
  function parsePriceFromText(priceText) {
    if (!priceText) return null;
    // Remove currency symbols, spaces, and extract number
    const cleanedPrice = priceText.replace(/[^0-9.,]/g, '').trim();
    if (!cleanedPrice) return null;

    // Handle both comma and period as decimal separators
    // Assume last separator is decimal if it's followed by exactly 2 digits
    let normalizedPrice = cleanedPrice;
    const lastComma = cleanedPrice.lastIndexOf(',');
    const lastPeriod = cleanedPrice.lastIndexOf('.');

    if (lastComma > lastPeriod && cleanedPrice.length - lastComma === 3) {
      // Comma is decimal separator (e.g., "29,99")
      normalizedPrice = cleanedPrice.replace(/\./g, '').replace(',', '.');
    } else {
      // Period is decimal separator or no decimal (e.g., "29.99" or "2999")
      normalizedPrice = cleanedPrice.replace(/,/g, '');
    }

    const price = parseFloat(normalizedPrice);
    if (isNaN(price) || price <= 0) return null;

    // Convert to cents (multiply by 100)
    return Math.round(price * 100);
  }

  /**
   * Extract product price from the page
   * @returns {number|null} Product price in cents, or null if not found
   */
  function getProductPrice() {
    // Return cached price if already retrieved (prevents cart update issues)
    if (window.shopacadoCachedProductPrice !== null) {
      return window.shopacadoCachedProductPrice;
    }

    let price = null;
    let priceSource = null;

    // ONLY use static sources that won't change with cart operations

    // Try 1: Shopify product JSON (most reliable, static product data)
    const productJson = document.querySelector('[data-product-json], script[type="application/json"][data-product-json], #ProductJson-product-template');
    if (productJson) {
      try {
        const product = JSON.parse(productJson.textContent);
        if (product && product.price) {
          price = parseFloat(product.price);
          priceSource = 'product-json (product.price)';
        } else if (product && product.selected_or_first_available_variant && product.selected_or_first_available_variant.price) {
          price = parseFloat(product.selected_or_first_available_variant.price);
          priceSource = 'product-json (variant.price)';
        }
      } catch (e) {
        // Product JSON parse failed
      }
    }

    // Try 2: meta tag with product price (static, set on page load)
    if (price === null) {
      const metaPrice = document.querySelector('meta[property="product:price:amount"]');
      if (metaPrice && metaPrice.content) {
        // Meta price is usually in dollars/pounds, convert to cents
        price = parseFloat(metaPrice.content) * 100;
        priceSource = 'meta[product:price:amount]';
      }
    }

    // Try 3: data-cv-price from MAIN product (not upsells/cards)
    // Look in main product containers first to avoid getting upsell prices
    if (price === null) {
      // Try main product containers first
      const mainProductSelectors = [
        '.product-info .js-prod-btn-group[data-cv-price]',
        '.product__info-wrapper .js-prod-btn-group[data-cv-price]',
        '#MainProduct .js-prod-btn-group[data-cv-price]',
        '.product-single .js-prod-btn-group[data-cv-price]',
        'form[action*="/cart/add"] .js-prod-btn-group[data-cv-price]'
      ];

      for (const selector of mainProductSelectors) {
        const btnGroup = document.querySelector(selector);
        if (btnGroup && btnGroup.dataset.cvPrice) {
          const cvPrice = parseFloat(btnGroup.dataset.cvPrice);
          if (cvPrice > 0) {
            price = cvPrice;
            priceSource = `data-cv-price (${selector})`;
            break;
          }
        }
      }

      // Fallback: find the first one (original behavior)
      if (price === null) {
        const btnGroup = document.querySelector('.js-prod-btn-group[data-cv-price]');
        if (btnGroup && btnGroup.dataset.cvPrice) {
          const cvPrice = parseFloat(btnGroup.dataset.cvPrice);
          if (cvPrice > 0) {
            price = cvPrice;
            priceSource = 'data-cv-price (first found)';
          }
        }
      }
    }

    // Try 4: data-product-price attribute (common in many themes)
    if (price === null) {
      const productPriceSelectors = [
        '[data-product-price]',
        '#ProductPrice[data-product-price]',
        '.product-price[data-product-price]'
      ];

      for (const selector of productPriceSelectors) {
        const el = document.querySelector(selector);
        if (el && el.dataset.productPrice) {
          const dataPrice = parseFloat(el.dataset.productPrice);
          if (dataPrice > 0) {
            price = dataPrice;
            priceSource = `data-product-price (${selector})`;
            break;
          }
        }
      }
    }

    // Try 5: Parse price from visible price elements (last resort)
    if (price === null) {
      const priceTextSelectors = [
        '.product-info .cc-price-amount.js-inc-vat',
        '.price__regular .money',
        '.price .money',
        '.product__price .money',
        '.product-single__price .money',
        '.price--regular .money',
        '[data-product-price]',
        '.product-price',
        '.current-price'
      ];

      for (const selector of priceTextSelectors) {
        const el = document.querySelector(selector);
        if (el) {
          const parsedPrice = parsePriceFromText(el.textContent);
          if (parsedPrice && parsedPrice > 0) {
            price = parsedPrice;
            priceSource = `parsed from text (${selector}): "${el.textContent.trim()}"`;
            break;
          }
        }
      }
    }

    // Cache the result if valid
    if (price !== null && price > 0) {
      window.shopacadoCachedProductPrice = price;
    }

    return price;
  }

  /**
   * Extract VAT percentage from the page
   * @returns {number} VAT percentage
   */
  function getVATPercentage() {
    let vatPercentage = null;
    let vatSource = null;

    // Try 1: data-vatrate attribute on product-info (multiplier format like 1.20)
    const productInfo = document.querySelector('.product-info[data-vatrate]');
    if (productInfo && productInfo.dataset.vatrate) {
      const vatMultiplier = parseFloat(productInfo.dataset.vatrate);
      if (vatMultiplier > 1) {
        // Convert multiplier (1.20) to percentage (20)
        vatPercentage = (vatMultiplier - 1) * 100;
        vatSource = 'data-vatrate attribute';
      }
    }

    // Try 2: Hidden input .js-vat-percentage (from theme.liquid)
    if (vatPercentage === null) {
      const vatInput = document.querySelector('.js-vat-percentage, input.js-vat-percentage');
      if (vatInput && vatInput.value) {
        const parsedVat = parseFloat(vatInput.value);
        if (!isNaN(parsedVat) && parsedVat > 0) {
          vatPercentage = parsedVat;
          vatSource = '.js-vat-percentage input';
        }
      }
    }

    // Try 3: Global Shopify settings (if available)
    if (vatPercentage === null && typeof window.Shopify !== 'undefined') {
      if (window.Shopify.vatPercentage) {
        vatPercentage = parseFloat(window.Shopify.vatPercentage);
        vatSource = 'window.Shopify.vatPercentage';
      }
    }

    // Default fallback
    if (vatPercentage === null) {
      vatPercentage = 20;
    }

    return vatPercentage;
  }

  /**
   * Set failure reason on element for debugging
   * @param {HTMLElement} element - The discount table block element
   * @param {string} reason - Failure reason
   */
  function setFailureReason(element, reason) {
    element.dataset.shopacadoFailure = reason;

    // Hide the block completely on failure so frontend looks clean
    element.style.display = 'none';

    // Also hide loading spinner if present
    const loadingSpinner = element.querySelector('.shopacado-table-loading');
    if (loadingSpinner) {
      loadingSpinner.style.display = 'none';
    }
  }

  /**
   * Enrich block element with required data attributes
   * @param {HTMLElement} element - The discount table block element
   * @returns {boolean} True if successfully enriched, false otherwise
   */
  function enrichBlockWithData(element) {

    // Always get price from static sources (product JSON, meta tag) - never from server attribute
    // This ensures the price never changes regardless of cart operations
    const productPrice = getProductPrice();
    const vatPercentage = getVATPercentage();

    if (!productPrice) {
      setFailureReason(element, 'PRICE_NOT_FOUND - Could not detect product price from any source');
      return false;
    }

    if (productPrice <= 0) {
      setFailureReason(element, 'PRICE_INVALID - Product price is zero or negative');
      return false;
    }

    // Apply data attributes (always use static price, ignore any server-rendered value)
    element.dataset.productPriceIncVat = productPrice;
    element.dataset.vatPercentage = vatPercentage;

    return true;
  }

  /**
   * Format price using Shopify money format
   * @param {number} cents - Price in cents
   * @param {string} format - Money format template (e.g., "£{{amount}}")
   * @returns {string} Formatted price string
   */
  function formatMoney(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }

    const value = (cents / 100).toFixed(2);
    const placeholderRegex = /\{\{\s*amount\s*\}\}/;

    return format.replace(placeholderRegex, value);
  }

  /**
   * Extract discount tier data from .adp-vol-wrapper (storefront embed)
   * @param {HTMLElement} wrapper - The .adp-vol-wrapper element
   * @returns {Array} Array of tier objects { qty, discount }
   */
  function extractDataFromAdpWrapper(wrapper) {
    const tiers = [];

    // Try to find the table - multiple possible selectors
    let table = wrapper.querySelector('.adp-discount-table');
    if (!table) {
      table = wrapper.querySelector('table');
    }
    if (!table) {
      return tiers;
    }

    // Get all rows from tbody (or all tr if no tbody)
    let rows = table.querySelectorAll('tbody tr');
    if (rows.length === 0) {
      rows = table.querySelectorAll('tr');
    }

    rows.forEach((row, index) => {
      // Try specific class selectors first
      let qtyCell = row.querySelector('.adp-vol-qty');
      let priceCell = row.querySelector('.adp-vol-price');

      // Fallback: use first and second td
      const allCells = row.querySelectorAll('td');
      if (!qtyCell && allCells.length >= 1) {
        qtyCell = allCells[0];
      }
      if (!priceCell && allCells.length >= 2) {
        priceCell = allCells[1];
      }

      if (qtyCell && priceCell) {
        const qty = qtyCell.textContent.trim();
        const priceText = priceCell.textContent.trim();

        // Parse discount from "3% off", "10% off", "3%", etc.
        const discountMatch = priceText.match(/(\d+(?:\.\d+)?)\s*%/);
        const discount = discountMatch ? parseFloat(discountMatch[1]) : 0;

        if (qty) {
          // Normalize quantity format
          let normalizedQty = qty;

          // Handle "2-+" format → "2 +"
          if (qty.match(/^(\d+)\s*-\s*\+$/)) {
            normalizedQty = qty.match(/^(\d+)/)[1] + ' +';
          }
          // Handle "5-9" or "5 - 9" format → "5 - 9"
          else if (qty.match(/^(\d+)\s*-\s*(\d+)$/)) {
            const parts = qty.match(/^(\d+)\s*-\s*(\d+)$/);
            normalizedQty = parts[1] + ' - ' + parts[2];
          }
          // Handle "10+" format → "10 +"
          else if (qty.match(/^(\d+)\s*\+$/)) {
            normalizedQty = qty.match(/^(\d+)/)[1] + ' +';
          }

          tiers.push({ qty: normalizedQty, discount });
        }
      }
    });

    return tiers;
  }

  /**
   * Build a Shopacado discount table block from extracted tier data
   * @param {Array} tiers - Array of tier objects { qty, discount }
   * @returns {HTMLElement} The new .shopacado-discount-table-block element
   */
  function buildShopacadoTable(tiers) {
    // Create the main block container
    const block = document.createElement('div');
    block.className = 'shopacado-discount-table-block';

    // Create the header
    const header = document.createElement('h4');
    header.textContent = 'Buy More, Pay Less';
    block.appendChild(header);

    // Create the table
    const table = document.createElement('table');
    table.className = 'shopacado-discount-table';

    // Create thead - only Quantity and Discount columns
    // Price columns will be added by initDiscountTableBlock
    const thead = document.createElement('thead');
    const headerRow = document.createElement('tr');

    const headers = ['Quantity', 'Discount'];
    headers.forEach(text => {
      const th = document.createElement('th');
      th.textContent = text;
      headerRow.appendChild(th);
    });

    thead.appendChild(headerRow);
    table.appendChild(thead);

    // Create tbody
    const tbody = document.createElement('tbody');

    tiers.forEach(tier => {
      const row = document.createElement('tr');

      // Quantity cell
      const qtyCell = document.createElement('td');
      qtyCell.textContent = tier.qty;
      row.appendChild(qtyCell);

      // Discount cell - with data attribute for price calculation
      const discountCell = document.createElement('td');
      discountCell.textContent = `${tier.discount}%`;
      discountCell.setAttribute('data-shopacado-discount-val', tier.discount);
      discountCell.setAttribute('data-money-format', '£{{amount}}');
      row.appendChild(discountCell);

      tbody.appendChild(row);
    });

    table.appendChild(tbody);
    block.appendChild(table);

    return block;
  }

  /**
   * Initialize a single discount table block
   * @param {HTMLElement} element - The discount table block element
   */
  function initDiscountTableBlock(element) {
    try {
      // Check if already initialized to prevent duplicate columns
      if (element.dataset.shopacadoInitialized === 'true') {
        return;
      }

      // Get the table element
      const table = element.querySelector('.shopacado-discount-table');
      if (!table) {
        setFailureReason(element, 'TABLE_NOT_FOUND - No .shopacado-discount-table element found inside block');
        return;
      }

      // Additional check: if table already has more than 4 columns (Qty, Discount, Save, Price inc, Price ex), it's already initialized
      const headerCells = table.querySelectorAll('thead th');
      if (headerCells.length > 4) {
        element.dataset.shopacadoInitialized = 'true';
        return;
      }

      // Apply inline styles as backup
      applyInlineStyles(element);

      // First, enrich the element with data attributes from the page
      const enriched = enrichBlockWithData(element);
      if (!enriched) {
        // Failure reason already set by enrichBlockWithData
        return;
      }

      // Get data attributes from block
      const basePriceIncVat = parseFloat(element.dataset.productPriceIncVat);
      const vatPercentage = parseFloat(element.dataset.vatPercentage);

      // Validate required data
      if (!basePriceIncVat || isNaN(basePriceIncVat)) {
        setFailureReason(element, 'BASE_PRICE_INVALID - basePriceIncVat is invalid or NaN');
        return;
      }

      if (!vatPercentage || isNaN(vatPercentage)) {
        setFailureReason(element, 'VAT_INVALID - vatPercentage is invalid or NaN');
        return;
      }

      // Calculate VAT multiplier (e.g., 1.20 for 20% VAT)
      const vatMultiplier = 1 + (vatPercentage / 100);

      // Add column headers
      const thead = table.querySelector('thead tr');
      let addedSaveColumn = false;

      if (thead) {
        // Check if Save column already exists
        const existingHeaders = Array.from(thead.querySelectorAll('th'));
        const hasSaveColumn = existingHeaders.some(th =>
          th.textContent.trim().toLowerCase() === 'save'
        );

        // Only add Save header if it doesn't exist
        if (!hasSaveColumn) {
          const saveHeader = document.createElement('th');
          saveHeader.textContent = 'Save';
          saveHeader.style.background = 'black';
          saveHeader.style.color = 'white';
          saveHeader.style.fontWeight = '700';
          saveHeader.style.fontSize = '13px';
          saveHeader.style.height = '30px';
          saveHeader.style.textAlign = 'center';
          thead.appendChild(saveHeader);
          addedSaveColumn = true;
        }

        // Price (inc. VAT) header
        const priceIncVatHeader = document.createElement('th');
        priceIncVatHeader.textContent = 'Price (inc. VAT)';
        priceIncVatHeader.style.background = '#A9D9C5';
        priceIncVatHeader.style.color = '#0F6B6B';
        priceIncVatHeader.style.fontWeight = '700';
        priceIncVatHeader.style.fontSize = '13px';
        priceIncVatHeader.style.height = '30px';
        priceIncVatHeader.style.textAlign = 'center';
        thead.appendChild(priceIncVatHeader);

        // Price (ex. VAT) header
        const priceExcVatHeader = document.createElement('th');
        priceExcVatHeader.textContent = 'Price (ex. VAT)';
        priceExcVatHeader.style.background = '#A9D9C5';
        priceExcVatHeader.style.color = '#0F6B6B';
        priceExcVatHeader.style.fontWeight = '700';
        priceExcVatHeader.style.fontSize = '13px';
        priceExcVatHeader.style.height = '30px';
        priceExcVatHeader.style.textAlign = 'center';
        priceExcVatHeader.style.borderBottomRightRadius = '5px';
        thead.appendChild(priceExcVatHeader);
      }

      // Add price cells to each row
      const rows = table.querySelectorAll('tbody tr');
      rows.forEach((row) => {
        const cells = row.querySelectorAll('td');
        if (cells.length < 2) return;

        // Find the discount percentage from data-shopacado-discount-val attribute
        let discountPercent = 0;
        let moneyFormat = '£{{amount}}'; // Default format

        for (let cell of cells) {
          if (cell.hasAttribute('data-shopacado-discount-val')) {
            discountPercent = parseFloat(cell.dataset.shopacadoDiscountVal) || 0;
            moneyFormat = cell.dataset.moneyFormat || moneyFormat;
            break;
          }
        }

        // Calculate prices
        // Save amount = base price × (discount% / 100) - per piece
        const saveAmount = basePriceIncVat * (discountPercent / 100);

        // Price Inc VAT = base price × (1 - discount%)
        // Note: basePriceIncVat is already in cents
        const priceIncVat = basePriceIncVat * (1 - discountPercent / 100);

        // Price Exc VAT = Price Inc VAT ÷ VAT multiplier
        const priceExcVat = priceIncVat / vatMultiplier;

        // Round to nearest cent (already in cents, no conversion needed)
        const saveAmountCents = Math.round(saveAmount);
        const priceIncVatCents = Math.round(priceIncVat);
        const priceExcVatCents = Math.round(priceExcVat);

        // Only add Save cell if we added the Save header (avoid duplicates)
        if (addedSaveColumn) {
          const saveCell = document.createElement('td');
          saveCell.className = 'shopacado-custom-table-val';
          saveCell.textContent = formatMoney(saveAmountCents, moneyFormat);
          saveCell.style.fontSize = '14px';
          saveCell.style.fontWeight = '600';
          saveCell.style.height = '30px';
          saveCell.style.textAlign = 'center';
          row.appendChild(saveCell);
        }

        // Create and append Price (inc. VAT) cell
        const priceIncVatCell = document.createElement('td');
        priceIncVatCell.className = 'shopacado-custom-table-val';
        priceIncVatCell.textContent = formatMoney(priceIncVatCents, moneyFormat);
        priceIncVatCell.style.fontSize = '14px';
        priceIncVatCell.style.fontWeight = '600';
        priceIncVatCell.style.height = '30px';
        priceIncVatCell.style.textAlign = 'center';
        row.appendChild(priceIncVatCell);

        // Create and append Price (ex. VAT) cell
        const priceExcVatCell = document.createElement('td');
        priceExcVatCell.className = 'shopacado-custom-table-val';
        priceExcVatCell.textContent = formatMoney(priceExcVatCents, moneyFormat);
        priceExcVatCell.style.fontSize = '14px';
        priceExcVatCell.style.fontWeight = '600';
        priceExcVatCell.style.height = '30px';
        priceExcVatCell.style.textAlign = 'center';
        row.appendChild(priceExcVatCell);
      });

      // Add "Need More" row as the very last row
      const tbody = table.querySelector('tbody');
      if (tbody) {
        const needMoreRow = document.createElement('tr');
        const needMoreCell = document.createElement('td');
        needMoreCell.setAttribute('colspan', '5');
        needMoreCell.className = 'shopacado-custom-table-val';
        needMoreCell.innerHTML = '<a href="tel:01279460460" style="color: #0F6B6B; cursor: pointer;">Need More? Call Us</a>';

        // Apply inline styles
        needMoreCell.style.fontSize = '14px';
        needMoreCell.style.fontWeight = '600';
        needMoreCell.style.height = '30px';
        needMoreCell.style.textAlign = 'center';

        needMoreRow.appendChild(needMoreCell);
        tbody.appendChild(needMoreRow);
      }

      // Mark as initialized to prevent re-processing
      element.dataset.shopacadoInitialized = 'true';
      // Clear any previous failure reason
      delete element.dataset.shopacadoFailure;

    } catch (error) {
      // Log error but don't break the page
      setFailureReason(element, `EXCEPTION - ${error.message}`);
      console.error('[Shopacado Error]', error);
    }
  }

  /**
   * Find and initialize all discount table blocks
   */
  function init() {
    // Inject CSS styles first
    injectStyles();

    // Find existing app blocks (.shopacado-discount-table-block)
    let appBlocks = document.querySelectorAll('.shopacado-discount-table-block');

    // Find .adp-vol-wrapper elements (storefront embed)
    const adpWrappers = document.querySelectorAll('.adp-vol-wrapper');

    // If .adp-vol-wrapper exists but NO app blocks exist, extract data and build Shopacado table
    if (adpWrappers.length > 0 && appBlocks.length === 0) {
      // Use the last .adp-vol-wrapper for data extraction
      const sourceWrapper = adpWrappers[adpWrappers.length - 1];
      const tiers = extractDataFromAdpWrapper(sourceWrapper);

      if (tiers.length > 0) {
        // Build new Shopacado table with extracted data
        const newBlock = buildShopacadoTable(tiers);

        // Insert after the delivery/stock section for consistent positioning
        const deliverySection = document.querySelector('.cc__deliveryinfo');
        if (deliverySection && deliverySection.parentElement) {
          deliverySection.parentElement.insertAdjacentElement('afterend', newBlock);
        } else {
          // Fallback: insert before the first .adp-vol-wrapper
          adpWrappers[0].parentNode.insertBefore(newBlock, adpWrappers[0]);
        }

        // Hide all .adp-vol-wrapper elements
        adpWrappers.forEach(wrapper => {
          wrapper.style.display = 'none';
        });

        // Re-query app blocks to include the new one
        appBlocks = document.querySelectorAll('.shopacado-discount-table-block');
      } else {
        adpWrappers.forEach(wrapper => {
          wrapper.style.display = 'none';
        });
      }
    } else if (adpWrappers.length > 0 && appBlocks.length > 0) {
      // App block exists, just hide .adp-vol-wrapper
      adpWrappers.forEach(wrapper => {
        wrapper.style.display = 'none';
      });
    }

    // Process all app blocks
    if (appBlocks.length === 0) {
      return;
    }

    appBlocks.forEach((block, index) => {

      // Inject loading spinner if it doesn't already exist
      if (!block.querySelector('.shopacado-table-loading')) {
        const loadingDiv = document.createElement('div');
        loadingDiv.className = 'shopacado-table-loading';
        loadingDiv.innerHTML = `
          <div class="shopacado-spinner"></div>
          <p class="shopacado-loading-text">Loading discount table...</p>
        `;

        // Insert as first child (before h4 and table)
        block.insertBefore(loadingDiv, block.firstChild);
      }

      initDiscountTableBlock(block);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // DOM already loaded, run immediately
    init();
  }

  // Watch for dynamically injected Shopacado elements
  const observer = new MutationObserver((mutations) => {
    let adpWrapperAdded = false;

    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) {
          // Check for dynamically added .adp-vol-wrapper
          if (node.classList && node.classList.contains('adp-vol-wrapper')) {
            adpWrapperAdded = true;
          }
          // Also check for nested .adp-vol-wrapper elements
          if (node.querySelectorAll) {
            const nested = node.querySelectorAll('.adp-vol-wrapper');
            if (nested.length > 0) {
              adpWrapperAdded = true;
            }
          }
          // Check for dynamically added app blocks and initialize them
          if (node.classList && node.classList.contains('shopacado-discount-table-block')) {
            initDiscountTableBlock(node);
          }
        }
      });
    });

    // If new .adp-vol-wrapper elements were added, use same logic as init()
    if (adpWrapperAdded) {
      const allAdpWrappers = document.querySelectorAll('.adp-vol-wrapper');
      const existingAppBlocks = document.querySelectorAll('.shopacado-discount-table-block');

      if (allAdpWrappers.length > 0 && existingAppBlocks.length === 0) {
        // No app blocks exist - extract data and build Shopacado table
        const sourceWrapper = allAdpWrappers[allAdpWrappers.length - 1];
        const tiers = extractDataFromAdpWrapper(sourceWrapper);

        if (tiers.length > 0) {
          const newBlock = buildShopacadoTable(tiers);

          // Insert after the delivery/stock section for consistent positioning
          const deliverySection = document.querySelector('.cc__deliveryinfo');
          if (deliverySection && deliverySection.parentElement) {
            deliverySection.parentElement.insertAdjacentElement('afterend', newBlock);
          } else {
            // Fallback: insert before the first .adp-vol-wrapper
            allAdpWrappers[0].parentNode.insertBefore(newBlock, allAdpWrappers[0]);
          }

          // Initialize the new block
          initDiscountTableBlock(newBlock);
        }

        // Hide all .adp-vol-wrapper
        allAdpWrappers.forEach(wrapper => {
          wrapper.style.display = 'none';
        });
      } else if (allAdpWrappers.length > 0) {
        // App block exists - just hide .adp-vol-wrapper
        allAdpWrappers.forEach(wrapper => {
          wrapper.style.display = 'none';
        });
      }
    }
  });

  // Start observing once DOM is ready
  if (document.body) {
    observer.observe(document.body, { childList: true, subtree: true });
  } else {
    document.addEventListener('DOMContentLoaded', () => {
      observer.observe(document.body, { childList: true, subtree: true });
    });
  }
})();
